import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link, Stack, IconButton, InputAdornment, TextField, Checkbox, Typography, CircularProgress, styled } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Swal from 'sweetalert2'; // Import Swal
import Api from '../../../utils/Api';
import Iconify from '../../../components/iconify';
import CAlert from '../../../components/CAlert/CAlert';
import { useDispatch, useSelector } from 'react-redux';
import { resetData, userLogin } from '../../../redux/features/userSlice';
import { Link as RouterLink } from 'react-router-dom';

// ----------------------------------------------------------------------

const StyledCheckbox = styled(Checkbox)({
  color: "#0BD349",
  "&.Mui-checked": {
    color: "#0BD349",
  },
  "&:hover": {
    backgroundColor: "rgba(11, 211, 73, 0.1)",
  },
});

export default function LoginForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  const { message } = useSelector((state) => state.user);
  const [messageError, setMessageError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Loading state for login
  const [isResetLoading, setIsResetLoading] = useState(false); // Loading state for password reset

  const handleClick = async (ev) => {
    ev.preventDefault();
    setIsLoading(true); // Start loading
    dispatch(userLogin(loginData)).then((res) => {
      setIsLoading(false); // Stop loading
      if (res && res.type === 'admin/user/fulfilled') {
        // window.location.replace('/dashboard/app');
        navigate('/dashboard/app')
      }
      if (res && res.type === 'admin/user/rejected') {
        setError(true);
        if (res.payload && res.payload.message) {
          setMessageError(res.payload.message);
        } else {
          setMessageError('Login failed. Please check your credentials.');
        }
        setTimeout(() => {
          dispatch(resetData());
          setError(false);
        }, 3000);
      }
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle Forgot Password
  const handleForgotPassword = async () => {
    const email = loginData.email;

    if (!email) {
      Swal.fire({
        icon: 'error',
        title: 'Fehler',
        text: 'Bitte geben Sie Ihre E-Mail-Adresse ein, um das Passwort zurückzusetzen',
        iconColor: '#000',
        confirmButtonColor: '#000'
      });
      return;
    }

    setIsResetLoading(true); // Start loading for reset
    try {
      const res = await Api.post('/password-reset', { email }); // Adjust this endpoint to match your backend
      setIsResetLoading(false); // Stop loading
      if (res.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Erfolg',
          text: 'Ein Link zum Zurücksetzen des Passworts wurde an Ihre E-Mail-Adresse gesendet.',
          iconColor: '#000',
          confirmButtonColor: '#000'
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Fehler',
          text: 'E-Mail zum Zurücksetzen des Passworts konnte nicht gesendet werden. Versuchen Sie es später noch einmal.',
          iconColor: '#000',
          confirmButtonColor: '#000'
        });
      }
    } catch (error) {
      setIsResetLoading(false); // Stop loading
      Swal.fire({
        icon: 'error',
        title: 'Fehler',
        text: 'Beim Senden der E-Mail zum Zurücksetzen des Passworts ist ein Fehler aufgetreten.',
        iconColor: '#000',
        confirmButtonColor: '#000'
      });
    }
  };

  const alertMessage = (
    <div style={{ margin: '10px 0px' }}>
      <CAlert text={message && message} type='error' />
    </div>
  );

  return (
    <form onSubmit={handleClick}>
      <Stack spacing={3}>
        <img
          src="/assets/ennovation_logo.png"
          alt="Logo"
          sx={{
            display: 'block',
            margin: '0 auto',
            height: { xs: '80px', sm: '100px', md: '120px' }, // Adjust height based on screen size
            width: { xs: '200px', sm: '300px', md: '380px' }, // Adjust width based on screen size
            objectFit: 'contain',
          }}
        />
        <Typography variant="h4" gutterBottom style={{ textAlign: 'center', paddingBottom: '20px' }}>
          {/* Sign In */}
          Anmeldung für Aussteller
        </Typography>
        <TextField name="email" label="E-Mail" onChange={handleChange} />
        <TextField
          name="password"
          // label="Password"
          label="Passwort"
          type={showPassword ? 'text' : 'password'}
          onChange={handleChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }} // Stack direction changes based on screen size
        alignItems="center"
        justifyContent="space-between"
        sx={{ my: 2 }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          sx={{
            cursor: 'pointer',
            mb: { xs: 1, sm: 0 },
            width: { xs: '100%', sm: 'fit-content' },
            flexDirection: { xs: 'row-reverse', sm: 'row' },
          }} // Add margin-bottom for small screens
        >
          <StyledCheckbox
            name="remember"
            id="remember"
            label="Remember me"
          />
          <label htmlFor="remember">
            {/* Remember me */}
            Daten speichern
          </label>
        </Stack>

        <Link
          variant="subtitle2"
          underline="hover"
          sx={{
            color: '#fff',
            cursor: 'pointer',
            textAlign: { xs: 'center', sm: 'right' }, // Center text on small screens, right-align on larger screens
          }}
          onClick={handleForgotPassword}
        >
          {isResetLoading ? <CircularProgress size={20} /> : 'Passwort vergessen?'}
        </Link>
      </Stack>
      {message && alertMessage}
      <LoadingButton
        sx={{
          boxShadow: 'none',
          '&:focus': {
            boxShadow: 'none', // Remove the blue shadow
          },
          '&:hover': {
            boxShadow: 'none', // Optionally remove shadow on hover too
          },
        }}
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        style={{ backgroundColor: '#0BD349', color: '#000000' }}
        loading={isLoading}>
        Login
      </LoadingButton>
      <Typography
        variant="body2"
        sx={{
          mb: 5,
          textAlign: 'center',
          pt: 5
        }}
      >
        {/* Don’t have an account? */}
        Sie sind Austeller und haben noch kein Konto?
        <RouterLink to='/register' style={{ textDecoration: 'none', color: '#fff' }}>
          <Typography variant="subtitle2">
            {/* Register Now */}
            Registrieren Sie sich jetzt
          </Typography>
        </RouterLink>
      </Typography>
    </form>
  );
}